import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY, from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AuthUser, Profile, Token } from './authentication.interfaces';
import { serverBaseUrl } from '../../shared/helpers/functions';

declare const UIkit;
const baseUrl = serverBaseUrl(environment.baseUrl);

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  grantUrl = `${baseUrl}backend/oauth/token/`;
  revokeUrl = `${baseUrl}backend/oauth/revoke_token/`;
  profileUrl = `${environment.baseUrl}users/profile`;
  isUserAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  redirectUrl: string;

  constructor(private cookieService: CookieService, private http: HttpClient, private router: Router) {
  }

  checkIsUserAuthenticated(): void {
    const token = new URL(document.location.toString()).searchParams.get('user_token');
    if (token) {
      from(this.router.navigate([], { queryParams: { user_token: undefined }, queryParamsHandling: 'merge' })).pipe(
        switchMap(() => this._getAccessToken({
          refresh_token: token,
          grant_type: 'refresh_token'
        })),
        switchMap((token) => this.saveToken(token))
      ).subscribe()
    } else
      this.isUserAuthenticated.next(!!this.cookieService.get('access_token'));
  }

  getAccessToken(user: AuthUser) {
    const data = { ...user, grant_type: 'password' };
    return this._getAccessToken(data);
  }

  _getAccessToken(data: any): Observable<Token> {
    const stringifiedData = new URLSearchParams(Object.entries({
      ...data,
      client_id: environment.loginClientid,
      client_secret: environment.loginClientsecret
    }) as any).toString();

    const options = {
      headers: new HttpHeaders({
        Authorization: '',
        'Content-Type': 'application/x-www-form-urlencoded'
      })
    };

    return this.http.post<Token>(this.grantUrl, stringifiedData, options);
  }

  getProfile(): Observable<Profile> {
    return this.http.get<Profile>(this.profileUrl);
  }

  login(user: AuthUser): Observable<null> {
    return this.getAccessToken(user).pipe(switchMap((token) => this.saveToken(token)));
  }

  saveToken(token: Token) {
    this.setToken(token);
    return this.getProfile().pipe(
      switchMap((profile: Profile) => {
        localStorage.setItem('id', profile.pk);
        if (profile.is_staff) {
          if (profile.first_name && profile.last_name) {
            const username = `${profile.first_name} ${profile.last_name}`;
            this.setUsername(username);
          } else {
            this.setUsername(profile.email);
          }
          this.checkIsUserAuthenticated();
          this.router.navigateByUrl(this.redirectUrl);
          return EMPTY;
        } else {
          return this.revokeToken();
        }
      })
    );
  }

  removeCookies(): void {
    UIkit.offcanvas('#offcanvas').hide();
    setTimeout(() => {
      this.cookieService.deleteAll('/', null, null, 'Strict');
      this.cookieService.deleteAll('../', null, null, 'Strict');
      this.checkIsUserAuthenticated();
    }, 400);
  }

  revokeToken(): Observable<null> {
    const data = {
      token: this.cookieService.get('access_token'),
      client_id: environment.loginClientid,
      client_secret: environment.loginClientsecret
    };
    const stringifiedData = new URLSearchParams(Object.entries(data) as any).toString();

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    };

    return this.http.post<null>(this.revokeUrl, stringifiedData, options);
  }

  setToken(token: Token): void {
    this.cookieService.set('access_token', token.access_token, null, '/', null, null, 'Strict');
  }

  setUsername(username: string): void {
    this.cookieService.set('username', username, null, '/', null, null, 'Strict');
  }
}
